<template>
  <div class="py-4 lg:py-8 flex flex-col gap-6 w-full max-w-4xl">
    <!-- "Inplanning stoppen" offers its undo here -->
    <!-- inside the activity its own menu is the way back -->
    <NuxtLink v-if="!route.params.event_id" :to="backLink.to" class="self-start text-sm text-surface-600 dark:text-surface-300 hover:underline">
      <AppIcon icon="arrow-left" class="mr-1" /> {{ backLink.label }}
    </NuxtLink>

    <div v-if="loading" class="flex justify-center py-8"><ProgressSpinner /></div>

    <Message v-else-if="!campaign" severity="warn" :closable="false">{{ t('mail_campaign_not_found') }}</Message>

    <template v-else>
      <!-- What people see first (the subject); the name for yourself sits behind the ⋯ -->
      <header class="flex flex-col gap-2">
        <div class="flex flex-wrap items-center gap-x-3 gap-y-2">
          <h1 class="page-title text-3xl m-0 min-w-0 break-words">{{ title }}</h1>
          <MailCampaignStatusTag :status="dueNow ? 'sending' : campaign.status" />
          <span v-if="!isNews" class="text-sm text-surface-600 dark:text-surface-300">{{ t(copy.kind) }}</span>
          <StatusLabel v-if="campaign.review_held_at" tone="ice" :label="t('mail_invitation_held')" />
          <span v-if="editable" class="text-sm text-surface-600 dark:text-surface-300" aria-live="polite">
            {{ saveState === 'saving' ? t('mail_campaign_saving') : saveState === 'saved' ? t('mail_campaign_saved') : saveState === 'error' ? t('mail_campaign_save_failed') : '' }}
          </span>
          <span v-if="editable" class="ml-auto"><RowMenu :items="headItems" :name="title" /></span>
        </div>
        <div v-if="editable && nameOpen" class="max-w-sm">
          <Field :label="t('mail_campaign_name_internal')" v-slot="{ id }">
            <InputText :id="id" v-model="name" maxlength="120" />
          </Field>
        </div>
        <p v-else-if="!editable && previewSubject && previewSubject !== campaign.name" class="m-0 text-sm text-surface-600 dark:text-surface-300">{{ campaign.name }}</p>
        <p v-if="isInvitation && discountName" class="m-0 text-sm text-surface-600 dark:text-surface-300">{{ t('mail_invitation_codes_of', { discount: discountName }) }}</p>
      </header>

      <!-- Draft: three steps, one decision each. Back always, forward only past what is done -->
      <template v-if="campaign.status === 'draft'">
        <nav :aria-label="t('mail_campaign_steps')" class="flex flex-wrap gap-2">
          <!-- no border on a cut block (the corners clip it away); the number is a plain square, never an octagon -->
          <button v-for="(s, i) in steps" :key="s.value" type="button" :disabled="!s.reachable" :aria-current="s.current ? 'step' : undefined"
            class="cut-sm flex items-center gap-2.5 py-2 pl-2.5 pr-3.5 text-[13px]"
            :class="[s.reachable ? 'bg-surface-0 dark:bg-surface-900 text-ink dark:text-surface-0 cursor-pointer' : 'bg-transparent text-surface-600 dark:text-surface-300 cursor-default',
                     s.current ? 'font-bold' : 'font-medium']"
            style="--chamfer: 6px" @click="step = s.value">
            <span class="grid place-items-center size-6 shrink-0 text-xs font-bold"
              :class="s.current ? 'bg-ink text-white dark:bg-surface-0 dark:text-ink' : s.done ? 'bg-mint text-ink' : 'bg-ash-2 text-surface-600 dark:bg-surface-800 dark:text-surface-300'">
              <AppIcon v-if="s.done" icon="check" :aria-label="t('mail_campaign_step_done')" /><template v-else>{{ i + 1 }}</template>
            </span>
            {{ s.label }}
          </button>
        </nav>

        <!-- v-show, not v-if: every step stays mounted, so the count is there when you land on Versturen -->
        <section v-show="step === 'content'" class="cut bg-surface-0 dark:bg-surface-900 p-5 sm:p-6 flex flex-col gap-4">
          <template v-if="selectedTemplate && !pickOpen">
            <div class="flex flex-wrap items-start gap-3">
              <div class="flex-1 basis-60">
                <h2 class="text-lg font-bold m-0">{{ t('mail_campaign_content_title') }}</h2>
                <p class="m-0 text-sm text-surface-600 dark:text-surface-300">{{ t('mail_campaign_content_line') }}</p>
              </div>
              <Select v-if="languageOptions.length > 1" v-model="previewLang" :options="languageOptions"
                optionLabel="label" optionValue="code" size="small" :aria-label="t('mail_campaign_preview_language')" />
              <RowMenu v-if="editable" :items="contentItems" :name="t('mail_campaign_content_title')" />
            </div>
            <Field v-if="editable" :label="t('mail_subject')" v-slot="{ id }">
              <InputText :id="id" v-model="subjectDraft" maxlength="200" class="w-full sm:max-w-md" @input="subjectTouched = true" />
            </Field>
            <MailCampaignPreview :html="previewHtml" :subject="previewSubject" :loading="previewLoading" :error="previewError" />
            <div class="flex flex-wrap items-center gap-4">
              <Button v-if="canWrite" as="router-link" :to="editorLink" :label="t('mail_campaign_edit_mail')">
                <template #icon><AppIcon icon="pen" class="mr-2" /></template>
              </Button>
              <button v-if="nextOf('content')" type="button" :class="link" @click="step = nextOf('content')!">{{ nextLabel('content') }}</button>
              <button v-if="prevOf('content')" type="button" :class="link" @click="step = prevOf('content')!">{{ t('back') }}</button>
            </div>
          </template>

          <template v-else>
            <h2 class="text-lg font-bold m-0">{{ t('mail_campaign_pick_mail') }}</h2>
            <div v-if="editable" class="flex flex-wrap items-center gap-2">
              <span class="text-sm text-surface-600 dark:text-surface-300">{{ t('mail_campaign_new_mail_from') }}</span>
              <Button v-for="s in starters" :key="s.key" :label="starterName(s)" size="small" severity="secondary"
                :loading="busyPick === s.key" :disabled="!!busyPick" @click="pickStarter(s)" />
            </div>
            <!-- an invitation keeps its starter: a design has no code block -->
            <div v-if="designs.length && !isInvitation" class="flex flex-col gap-2">
              <span class="text-sm text-surface-600 dark:text-surface-300">{{ t('mail_campaign_from_design') }}</span>
              <button v-for="tpl in designs" :key="tpl.id" type="button" :disabled="!editable || !!busyPick"
                class="flex items-center gap-3 border border-surface-200 dark:border-surface-700 rounded-lg px-4 py-3 text-left hover:bg-surface-50 dark:hover:bg-surface-800 disabled:opacity-60"
                @click="pickDesign(tpl)">
                <span class="flex-1 font-medium">{{ tpl.name }}</span>
                <ProgressSpinner v-if="busyPick === tpl.id" class="!size-4" strokeWidth="6" />
                <span v-else class="text-sm text-surface-600 dark:text-surface-300 tabular-nums">{{ t('mail_campaign_edited', { when: dateTime(tpl.updated_at) }) }}</span>
              </button>
              <p class="m-0 text-sm text-surface-600 dark:text-surface-300">{{ t('mail_campaign_design_copied') }}</p>
            </div>
            <Message v-if="pickError" severity="error" size="small" role="alert">{{ pickError }}</Message>
            <button v-if="selectedTemplate" type="button" :class="link" class="self-start" @click="pickOpen = false">{{ t('back') }}</button>
          </template>
        </section>

        <section v-show="step === 'recipients'" class="cut bg-surface-0 dark:bg-surface-900 p-5 sm:p-6 flex flex-col gap-5">
          <div>
            <h2 class="text-lg font-bold m-0">{{ t('mail_campaign_recipients_title') }}</h2>
            <p v-if="isNews" class="m-0 text-sm text-surface-600 dark:text-surface-300 max-w-prose">{{ t('mail_campaign_recipients_intro') }}</p>
          </div>
          <MailSegmentServiceRecipients v-if="isService" v-model="segment" :disabled="!editable" @count="(c: SegmentCount | null) => (count = c)" />
          <!-- An invitation: people with consent, a group of them, or a list of your own (spec §7.6) -->
          <template v-else-if="isInvitation">
            <p v-if="campaign.parent_campaign_id" class="m-0 text-surface-600 dark:text-surface-300 max-w-prose">{{ t('mail_invitation_reminder_recipients') }}</p>
            <template v-else>
              <div class="flex flex-col gap-2">
                <span :id="`${uid}-who`" class="font-medium">{{ t('mail_invitation_who') }}</span>
                <Picker mode="pills" :model-value="recipientMode" :options="recipientModes" :disabled="!editable"
                  :labelledby="`${uid}-who`" @update:model-value="(m: unknown) => setRecipientMode(m as RecipientMode)" />
              </div>
              <p v-if="recipientMode === 'consent'" class="m-0 text-surface-600 dark:text-surface-300 max-w-prose">{{ t('mail_invitation_consent_line') }}</p>
              <MailSegmentBuilder v-else-if="recipientMode === 'group'" v-model="segment" :disabled="!editable" />
              <section v-else class="flex flex-col gap-3 max-w-xl">
                <label :for="`${uid}-list`" class="font-medium">{{ t('mail_invitation_list_label') }}</label>
                <Textarea :id="`${uid}-list`" v-model="listText" rows="6" :placeholder="t('mail_invitation_list_placeholder')"
                  :disabled="!editable" class="font-mono text-sm" />
                <div class="flex flex-wrap items-center gap-3">
                  <Button :label="t('mail_invitation_list_save')" size="small" :loading="savingList"
                    :disabled="!editable || !listText.trim()" @click="saveList" />
                  <Button type="button" text size="small" severity="secondary" :label="t('mail_invitation_list_csv')"
                    :disabled="!editable" @click="listFile?.click()">
                    <template #icon><AppIcon icon="file-arrow-up" class="mr-2" /></template>
                  </Button>
                  <input ref="listFile" type="file" accept=".csv,.txt" class="hidden" @change="onListFile" />
                </div>
                <Message v-if="listError" severity="error" size="small" role="alert">{{ listError }}</Message>
                <p v-if="listResult" class="m-0 text-sm tabular-nums">
                  {{ t('mail_invitation_list_saved', { n: num(listResult.saved) }, listResult.saved) }}
                  <template v-if="listResult.new_contacts"> · {{ t('mail_invitation_list_new', { n: num(listResult.new_contacts) }) }}</template>
                  <template v-if="listResult.unreachable"> · {{ t('mail_invitation_list_unreachable', { n: num(listResult.unreachable) }, listResult.unreachable) }}</template>
                </p>
                <p v-if="listResult?.invalid.length" class="m-0 text-sm text-plum dark:text-rose break-words">
                  {{ t('mail_invitation_list_invalid', { list: listResult.invalid.join(', ') }, listResult.invalid.length) }}
                </p>
                <label v-if="ownList" class="flex items-start gap-2">
                  <Checkbox binary :model-value="!!campaign.list_confirmed_at" :disabled="!editable || confirmingList" class="mt-0.5"
                    @update:model-value="confirmList" />
                  <span>{{ t('mail_invitation_list_confirm') }} *</span>
                </label>
              </section>
            </template>
            <p v-if="count && (recipientMode !== 'list' || ownList) && recipientMode !== 'group'" class="m-0 text-lg font-semibold tabular-nums" aria-live="polite">
              {{ t('mail_segment_count_reachable', { n: num(count.reachable) }, count.reachable) }}
            </p>
            <p class="m-0 text-sm text-surface-600 dark:text-surface-300">
              <AppIcon icon="shield-heart" class="mr-1" />{{ t('mail_invitation_unsubscribe_line') }}
            </p>
          </template>
          <MailSegmentBuilder v-else v-model="segment" :disabled="!editable" @count="(c: SegmentCount | null) => (count = c)" />

          <div class="flex flex-col gap-1.5">
            <div class="flex flex-wrap items-center gap-4">
              <Button v-if="nextOf('recipients')" :label="nextLabel('recipients')" :disabled="!recipientsReady" @click="step = nextOf('recipients')!" />
              <button v-if="prevOf('recipients')" type="button" :class="link" @click="step = prevOf('recipients')!">{{ t('back') }}</button>
            </div>
            <span v-if="recipientsBlock" class="text-sm text-surface-600 dark:text-surface-300">{{ recipientsBlock }}</span>
          </div>
        </section>

        <section v-show="step === 'send'" class="cut bg-surface-0 dark:bg-surface-900 p-5 sm:p-6 flex flex-col gap-5">
          <Message v-if="!canSend" severity="info" :closable="false">{{ t(copy.noSendRight) }}</Message>

          <div class="flex flex-col gap-1.5">
            <p class="m-0 text-lg leading-snug tabular-nums">
              <template v-if="!templateId">{{ t('mail_campaign_summary_no_mail_step') }}</template>
              <template v-else-if="!count?.reachable">{{ t(copy.noRecipients) }}</template>
              <!-- two bold parts of one weight: who and what -->
              <template v-else-if="isNews"><strong class="font-bold">{{ t('mail_campaign_summary_people', { n: num(count.reachable) }, count.reachable) }}</strong>
                {{ t('mail_campaign_summary_get', count.reachable) }} <strong class="font-bold">‘{{ shownSubject }}’</strong>, {{ whenText }}.</template>
              <template v-else>{{ t(copy.summary, { n: num(count.reachable), subject: shownSubject }, count.reachable) }}</template>
            </p>
            <p v-if="isService" class="m-0 text-sm text-surface-600 dark:text-surface-300">
              <AppIcon icon="shield-heart" class="mr-1" />{{ t('mail_service_reassure') }}
            </p>
            <p v-else-if="quota && count?.reachable && !quotaExceeded" class="m-0 text-sm text-surface-600 dark:text-surface-300 tabular-nums">
              {{ t('mail_campaign_quota_after', { used: num(quota.sent_this_month + count.reachable), limit: num(quota.monthly_limit) }) }}
            </p>
          </div>

          <!-- What blocks is plum with a way out, what is good to know ice, at most one yellow -->
          <Message v-if="quota && quotaExceeded" severity="error" :closable="false">
            {{ t('mail_campaign_quota_would_exceed', { left: num(Math.max(0, quota.monthly_limit - quota.sent_this_month)), limit: num(quota.monthly_limit) }) }}
          </Message>
          <MailCampaignLimitBanner v-if="!isService" :quota="quota" />
          <Message v-if="!isService && postalMissing" severity="error" :closable="false">
            <div class="flex flex-wrap items-center gap-3">
              <span class="flex-1 min-w-52"><strong>{{ t('mail_campaign_postal_missing_title') }}</strong> {{ t('mail_campaign_postal_why') }}</span>
              <Button v-if="canWrite" :label="t('mail_campaign_postal_fill')" size="small" severity="secondary" @click="postalOpen = true" />
            </div>
          </Message>
          <Message v-if="missingLanguages.length" severity="warn" :closable="false">
            {{ t('mail_campaign_translation_missing', { langs: missingLanguages.join(', ') }) }}
            <NuxtLink v-if="canWrite" :to="editorLink" class="underline font-medium">{{ t('mail_campaign_translation_fix') }}</NuxtLink>
          </Message>

          <div class="flex flex-col gap-2.5">
            <h3 class="text-sm font-semibold m-0">{{ t('mail_campaign_test_step') }}</h3>
            <MailCampaignTestSend :template-id="templateId" :kind="mailKind" :lang="previewLang" :event-id="serviceEventId"
              :done="testDone" :sent-to="test?.sent_to" :optional="isService" @sent="(r) => (test = r)" />
          </div>

          <div class="flex flex-col gap-2.5">
            <h3 :id="`${uid}-when`" class="text-sm font-semibold m-0">{{ t('mail_campaign_when_step') }}</h3>
            <div class="flex flex-wrap items-center gap-3">
              <Picker mode="pills" :model-value="later ? 'later' : 'now'" :options="whenOptions" :disabled="!canSend" :labelledby="`${uid}-when`"
                @update:model-value="(v: unknown) => (later = v === 'later')" />
              <template v-if="later">
                <DatePicker v-model="scheduleAt" showTime hourFormat="24" :stepMinute="15" :aria-label="t('mail_campaign_schedule_at')"
                  :minDate="new Date()" dateFormat="dd-mm-yy" showIcon iconDisplay="input" :disabled="!canSend" class="w-full sm:w-64" />
                <span class="text-sm text-surface-600 dark:text-surface-300">{{ timeZoneLabel }}</span>
              </template>
            </div>
          </div>

          <Message v-if="scheduleError" severity="error" :closable="false">
            {{ scheduleErrorText }}
            <NuxtLink v-if="scheduleError === 'mail_not_enabled'" to="/mail/overview" class="underline font-medium">{{ t('mail_overview') }}</NuxtLink>
          </Message>

          <div class="flex flex-col gap-1.5">
            <div class="flex flex-wrap items-center gap-4">
              <!-- black only once it can go: before that the test is the one black button -->
              <Button :label="later ? t('mail_campaign_schedule') : t('mail_campaign_send')" :severity="canSchedule ? undefined : 'secondary'"
                :disabled="!canSchedule" :loading="scheduling" @click="confirmSchedule">
                <template #icon><AppIcon icon="paper-plane" class="mr-2" /></template>
              </Button>
              <button v-if="prevOf('send')" type="button" :class="link" @click="step = prevOf('send')!">{{ t('back') }}</button>
            </div>
            <span v-if="sendBlock" class="text-sm text-surface-600 dark:text-surface-300">{{ sendBlock }}</span>
          </div>
        </section>

        <!-- The postal address is the organization's, filled in once: a small dialog, not a field in the step -->
        <Dialog v-model:visible="postalOpen" modal :header="t('mail_campaign_postal_label')" class="w-full max-w-md mx-4">
          <Field :label="t('mail_campaign_postal_address')" :hint="t('mail_campaign_postal_hint')" :error="postalError ?? undefined" v-slot="{ id, describedby }">
            <Textarea :id="id" v-model="postal" rows="3" autoResize :aria-describedby="describedby" />
          </Field>
          <template #footer>
            <Button :label="t('close')" severity="secondary" text @click="postalOpen = false" />
            <Button :label="t('mail_campaign_postal_save')" :loading="savingPostal" :disabled="!postal.trim()" @click="savePostal" />
          </template>
        </Dialog>
      </template>

      <!-- Scheduled, sending or sent: read-only, with how it went -->
      <template v-else>
        <section class="cut bg-surface-0 dark:bg-surface-900 p-5 sm:p-6 flex flex-col gap-4">
          <p v-if="campaign.status === 'scheduled' && campaign.scheduled_at && !dueNow" class="m-0 text-base tabular-nums">
            {{ t('mail_campaign_scheduled_line', { when: dateTime(campaign.scheduled_at), tz: timeZoneLabel, n: num(campaign.recipients ?? 0) }, campaign.recipients ?? 0) }}
          </p>
          <template v-if="counts">
            <template v-if="finished">
              <p class="m-0 text-base tabular-nums">
                {{ isService
                  ? t('mail_service_done', { n: num(arrived), target: serviceTarget }, arrived)
                  : t(isInvitation ? 'mail_invitation_done' : 'mail_campaign_done', { n: num(arrived) }, arrived) }}
                <template v-if="notWorking">{{ t('mail_campaign_done_failed', { n: num(notWorking) }, notWorking) }}</template>
              </p>
              <div class="grid grid-cols-2 gap-3 max-w-md">
                <div class="bg-[#F7F7F5] dark:bg-surface-800 p-3.5">
                  <div class="text-3xl font-extrabold tracking-tight tabular-nums">{{ num(campaign.recipients ?? totalCount) }}</div>
                  <div class="text-sm text-surface-600 dark:text-surface-300">{{ t('mail_campaign_tile_sent') }}</div>
                </div>
                <div class="bg-[#F7F7F5] dark:bg-surface-800 p-3.5">
                  <div class="text-3xl font-extrabold tracking-tight tabular-nums">{{ num(arrived) }}</div>
                  <div class="text-sm text-surface-600 dark:text-surface-300">{{ t('mail_campaign_tile_arrived') }}</div>
                </div>
              </div>
              <p v-if="campaign.sent_at" class="m-0 text-sm text-surface-600 dark:text-surface-300 tabular-nums">{{ t('mail_campaign_sent_at') }} {{ dateTime(campaign.sent_at) }}</p>
              <MailSales v-if="!isService" :campaign-id="campaign.id" />
              <p v-if="isService" class="m-0 text-sm">
                {{ t('mail_service_done_replies') }}
                <NuxtLink to="/mail/inbox" class="underline font-medium">{{ t('mail_inbox') }}</NuxtLink>
              </p>
            </template>
            <template v-else-if="campaign.status !== 'scheduled' || doneCount">
              <ProgressBar :value="progressPercent" :showValue="false" class="!h-2" :aria-label="t('mail_campaign_progress')" />
              <p class="m-0 tabular-nums">{{ t('mail_campaign_progress_line', { done: num(doneCount), total: num(totalCount) }) }}</p>
            </template>

            <!-- Why some did not get it, grouped; held-back content says what to do -->
            <ul v-if="skipped.length" class="m-0 p-0 list-none flex flex-col gap-1 text-sm">
              <li v-for="s in skipped" :key="s.reason" class="flex flex-col">
                <span><span class="tabular-nums font-medium">{{ num(s.n) }}</span> · {{ te(`mail_skip_${s.reason}`) ? t(`mail_skip_${s.reason}`) : t('mail_status_skipped') }}</span>
                <span v-if="s.reason === 'blocked_content'" class="text-surface-600 dark:text-surface-300">
                  {{ t('mail_campaign_blocked_help') }}
                  <a href="mailto:info@tickable.nl" class="underline font-medium">info@tickable.nl</a>
                </span>
              </li>
            </ul>
          </template>
          <ProgressSpinner v-else class="!size-6" strokeWidth="6" />

          <p v-if="campaign.review_held_at" class="m-0 text-sm text-surface-600 dark:text-surface-300 max-w-prose">{{ t('mail_invitation_held_line') }}</p>

          <div v-if="canCancel || (canWrite && campaign.status === 'sent')" class="flex flex-wrap items-center gap-4">
            <Button v-if="canCancel" :label="t('mail_campaign_unschedule')" severity="secondary" :loading="cancelling" @click="unschedule" />
            <Button v-if="canWrite && campaign.status === 'sent'" :label="t('mail_campaign_copy_next')" severity="secondary"
              :loading="copying === campaign.id" @click="copyCampaign(campaign)">
              <template #icon><AppIcon icon="copy" class="mr-2" /></template>
            </Button>
          </div>
        </section>

        <!-- Per person: dashed ring = mailed, full ring = bought (spec §7.6) -->
        <section v-if="isInvitation && report?.length" class="flex flex-col gap-3">
          <div class="flex flex-wrap items-center gap-3">
            <h2 class="text-xl font-semibold m-0 flex-1 basis-48">{{ t('mail_invitation_report_title') }}</h2>
            <Button v-if="unusedCodes.length" :label="t('mail_invitation_export_unused')" size="small" severity="secondary" @click="exportUnused">
              <template #icon><AppIcon icon="download" class="mr-2" /></template>
            </Button>
          </div>
          <p class="m-0 tabular-nums">{{ t('mail_invitation_report_counts', { mailed: num(mailedCount), bought: num(boughtCount) }) }}</p>
          <ul class="cut bg-surface-0 dark:bg-surface-900 list-none m-0 px-5 divide-y divide-surface-100 dark:divide-surface-800">
            <li v-for="r in report" :key="r.send_id" class="py-3 flex flex-wrap items-center gap-x-3 gap-y-1">
              <PersonAvatar :name="r.name" :email="r.email" :ring="r.order_id ? 'ticket' : ARRIVED.includes(r.status) ? 'waiting' : undefined" />
              <div class="flex-1 min-w-40">
                <div class="font-medium text-surface-900 dark:text-surface-0 truncate">{{ r.name || r.email }}</div>
                <div v-if="r.name" class="text-sm text-surface-600 dark:text-surface-300 truncate">{{ r.email }}</div>
              </div>
              <span v-if="r.code" class="font-mono text-sm">{{ r.code }}</span>
              <StatusLabel v-if="r.order_id" tone="mint" :label="t('mail_invitation_bought')" />
              <StatusLabel v-else-if="!ARRIVED.includes(r.status)" tone="ash" :label="te(`mail_status_${r.status}`) ? t(`mail_status_${r.status}`) : r.status" />
            </li>
          </ul>
          <div v-if="canWrite && finished" class="flex flex-wrap items-center gap-3">
            <Button :label="t('mail_invitation_remind')" :loading="reminding" :disabled="!notBoughtCount" @click="remind" />
            <span class="text-sm text-surface-600 dark:text-surface-300">{{ notBoughtCount ? t('mail_invitation_remind_help', { n: num(notBoughtCount) }, notBoughtCount) : t('mail_invitation_remind_nobody') }}</span>
          </div>
        </section>

        <section v-if="selectedTemplate" class="flex flex-col gap-3">
          <h2 class="text-xl font-semibold m-0">{{ t('mail_campaign_preview') }}</h2>
          <MailCampaignPreview :html="previewHtml" :subject="previewSubject" :loading="previewLoading" :error="previewError" />
        </section>
      </template>
    </template>
  </div>
</template>

<script lang="ts" setup>
import { useI18n } from 'vue-i18n';
import type { Database } from '~~/types/supabase';
import { MAIL_LANGUAGES, type MailCampaign, type MailTemplate, type MailDocument } from '~/composables/useMail';
import { STARTER_TEMPLATES, type Starter } from '~~/supabase/functions/_shared/mail/defaults';
import { fieldState, lFields, writeText } from '~/components/Mail/Editor/editor';
import type { Segment, SegmentCount } from '~/components/Mail/Segment/SegmentBuilder.vue';
import type { Quota } from '~/components/Mail/Campaign/LimitBanner.vue';
import formatTimeslot from '~/utils/formatTimeslot';

type StepKey = 'recipients' | 'content' | 'send';

// One wizard, two kinds: the texts that differ between a newsletter and a message to visitors
const COPY = {
  news: {
    kind: 'mail_campaign_kind_news',
    order: ['content', 'recipients', 'send'] as StepKey[],
    summary: 'mail_campaign_summary_line',
    noRecipients: 'mail_campaign_summary_no_recipients',
    noSendRight: 'mail_campaign_no_send_right',
    testIntro: 'mail_campaign_test_intro',
    // the question names how many and what happens; "Kan niet terug" is in the message
    confirmNowTitle: 'mail_campaign_confirm_news_now_title',
    confirmLaterTitle: 'mail_campaign_confirm_news_later_title',
    confirmNow: 'mail_campaign_confirm_news_now',
    confirmLater: 'mail_campaign_confirm_news_later',
  },
  service: {
    kind: 'mail_campaign_kind_service',
    order: ['recipients', 'content', 'send'] as StepKey[],
    summary: 'mail_service_summary_line',
    noRecipients: 'mail_service_summary_no_recipients',
    noSendRight: 'mail_service_no_send_right',
    testIntro: 'mail_service_test_intro',
    confirmNowTitle: 'mail_campaign_confirm_now_title',
    confirmLaterTitle: 'mail_campaign_confirm_later_title',
    confirmNow: 'mail_service_confirm_now',
    confirmLater: 'mail_service_confirm_later',
  },
  // "Codes mailen": who first, the mail comes ready with its personal code
  invitation: {
    kind: 'mail_campaign_kind_invitation',
    order: ['recipients', 'content', 'send'] as StepKey[],
    summary: 'mail_invitation_summary_line',
    noRecipients: 'mail_invitation_summary_no_recipients',
    noSendRight: 'mail_invitation_no_send_right',
    testIntro: 'mail_campaign_test_intro',
    confirmNowTitle: 'mail_campaign_confirm_now_title',
    confirmLaterTitle: 'mail_campaign_confirm_later_title',
    confirmNow: 'mail_campaign_confirm_now',
    confirmLater: 'mail_campaign_confirm_later',
  },
} as const;

const { t, te, locale } = useI18n();
const route = useRoute();
const router = useRouter();
const toast = useToast();
const confirmAction = useConfirmAction();
const supabase = useSupabaseClient<Database>();
const userStore = useUserStore();
const { organizationId, settings, loadSettings, saveSettings, preview, campaignMailFromStarter, copyCampaign, copying, languages: orgLanguages } = useMail();
const undo = useUndo();
const uid = useId();

const id = computed(() => String(route.params.id));
// A message to visitors opens inside its activity (/events/<id>/message/<id>), a newsletter under Publiek
const selfPath = computed(() => (route.params.event_id ? `/events/${route.params.event_id}/message/${id.value}` : `/mail/campaigns/${id.value}`));
const canWrite = computed(() => userStore.hasRight('mail.write'));
const canSend = computed(() => userStore.hasRight('mail.send'));

const { num, dateTime } = useLocaleFormat();

// ── Loading ─────────────────────────────────────────────────────────────────
const campaign = ref<MailCampaign | null>(null);
const selectedTemplate = ref<MailTemplate | null>(null);
const designs = ref<MailTemplate[]>([]);
const quota = ref<Quota | null>(null);
const loading = ref(true);

const name = ref('');
const segment = ref<Segment>({});
const templateId = ref<string | null>(null);
const count = ref<SegmentCount | null>(null);

const isService = computed(() => campaign.value?.kind === 'service');
const isInvitation = computed(() => campaign.value?.kind === 'invitation');
const isNews = computed(() => !isService.value && !isInvitation.value);
const copy = computed(() => COPY[isService.value ? 'service' : isInvitation.value ? 'invitation' : 'news']);
const mailKind = computed(() => (isService.value ? 'transactional' : 'marketing'));
const serviceEventId = computed(() => (isService.value ? segment.value.event_ids?.[0] ?? null : null));

const editable = computed(() => campaign.value?.status === 'draft' && canWrite.value);
// "Nu versturen" schedules for now: that is sending, not a planned moment
const dueNow = computed(() => campaign.value?.status === 'scheduled' && !!campaign.value.scheduled_at
  && new Date(campaign.value.scheduled_at).getTime() <= Date.now() + 60_000);
const snapshot = () => JSON.stringify({ name: name.value.trim(), segment: segment.value, template_id: templateId.value });
let lastSaved = '';

// For a message to visitors: the activity (and the one timeslot, when only one was picked)
const eventTitle = ref('');
const serviceTarget = ref('');
const loadServiceTarget = async () => {
  const eventId = serviceEventId.value;
  if (!eventId) return;
  const slotIds = segment.value.timeslot_ids ?? [];
  const [ev, slot] = await Promise.all([
    supabase.from('events').select('title').eq('id', eventId).single(),
    slotIds.length === 1 ? supabase.from('event_timeslots').select('*').eq('id', slotIds[0]!).maybeSingle() : null,
  ]);
  eventTitle.value = ev.data?.title ?? '';
  serviceTarget.value = slot?.data ? formatTimeslot(slot.data) : eventTitle.value;
};

const loadTemplate = async (tplId: string | null) => {
  selectedTemplate.value = null;
  test.value = null;
  if (!tplId) return;
  const [tpl, tested] = await Promise.all([
    supabase.from('mail_templates').select('*').eq('id', tplId).maybeSingle(),
    supabase.from('mail_template_tests').select('sent_to, template_updated_at').eq('template_id', tplId).maybeSingle(),
  ]);
  selectedTemplate.value = tpl.data;
  test.value = tested.data;
};

const discountName = ref('');
const loadCampaign = async () => {
  const { data } = await supabase.from('mail_campaigns').select('*, discounts(name)').eq('id', id.value).maybeSingle();
  const templateChanged = data?.template_id !== campaign.value?.template_id || !selectedTemplate.value;
  campaign.value = data;
  discountName.value = data?.discounts?.name ?? '';
  if (data) {
    name.value = data.name;
    segment.value = (data.segment ?? {}) as Segment;
    recipientMode.value = modeOf(segment.value);
    templateId.value = data.template_id;
    lastSaved = snapshot();
    if (templateChanged) await loadTemplate(data.template_id);
  }
};

const loadQuota = async () => {
  if (!organizationId.value) return;
  const { data } = await supabase.rpc('mail_marketing_quota', { p_org: organizationId.value });
  quota.value = (data as Quota[] | null)?.[0] ?? null;
};

// Designs of your own to start from (never another campaign's copy)
const loadDesigns = async () => {
  if (!organizationId.value) return;
  const { data } = await supabase.from('mail_templates').select('*').eq('organization_id', organizationId.value)
    .is('campaign_id', null).is('system_key', null).eq('kind', mailKind.value).order('updated_at', { ascending: false });
  designs.value = data ?? [];
};

const load = async () => {
  if (!organizationId.value) return;
  loading.value = true;
  await Promise.all([loadCampaign(), settings.value ? null : loadSettings()]);
  if (isService.value && serviceEventId.value && !route.params.event_id) {
    await router.replace({ path: `/events/${serviceEventId.value}/message/${id.value}`, query: route.query });
    return;
  }
  await Promise.all([loadDesigns(), isService.value ? loadServiceTarget() : loadQuota()]);
  const asked = route.query.step as StepKey | undefined;
  step.value = asked && copy.value.order.includes(asked) ? asked : copy.value.order[0]!;
  loading.value = false;
};

const backLink = computed(() => (isService.value && serviceEventId.value && eventTitle.value
  ? { to: `/events/${serviceEventId.value}`, label: eventTitle.value }
  : { to: '/mail/campaigns', label: t('mail_campaigns') }));

// ── Autosave ────────────────────────────────────────────────────────────────
const saveState = ref<'idle' | 'saving' | 'saved' | 'error'>('idle');
const save = async () => {
  if (!editable.value || !campaign.value) return;
  const current = snapshot();
  if (current === lastSaved) return;
  saveState.value = 'saving';
  // an emptied name keeps the stored one, so the recipients and the mail still save
  const { error } = await supabase.from('mail_campaigns')
    .update({ name: name.value.trim() || campaign.value.name || title.value, segment: segment.value as never, template_id: templateId.value })
    .eq('id', campaign.value.id);
  if (error) {
    // The status line beside the title says so
    saveState.value = 'error';
    console.error(error);
    return false;
  }
  lastSaved = current;
  saveState.value = 'saved';
  return true;
};
watchDebounced([name, segment, templateId], save, { debounce: 800, deep: true });

// ── Head ────────────────────────────────────────────────────────────────────
// the rendered subject, or the one in the field while the preview is not there (yet)
const shownSubject = computed(() => previewSubject.value || subjectDraft.value.trim());
const title = computed(() => shownSubject.value || (isNews.value ? t('mail_campaign_kind_news') : name.value || campaign.value?.name || ''));
const link = 'p-0 border-0 bg-transparent text-sm font-semibold underline underline-offset-4 cursor-pointer text-ink dark:text-surface-0';
const nameOpen = ref(false);
const headItems = computed(() => [{ label: t('mail_campaign_name_internal'), icon: 'pen', command: () => { nameOpen.value = true; } }]);

// ── Steps ───────────────────────────────────────────────────────────────────
// A newsletter starts with what you want to say; a message to visitors with who it is for.
const step = ref<StepKey>('content');
const stepDone = (s: StepKey) => (s === 'content' ? !!templateId.value : s === 'recipients' ? recipientsReady.value : false);
// Done is what lies behind you; ahead only as far as everything before it is done
const steps = computed(() => {
  const order = copy.value.order;
  const current = order.indexOf(step.value);
  return order.map((value, i) => ({
    value,
    label: t(`mail_campaign_rail_${value}`),
    current: i === current,
    done: i < current && stepDone(value),
    reachable: i <= current || order.slice(0, i).every(stepDone),
  }));
});
const prevOf = (s: StepKey) => copy.value.order[copy.value.order.indexOf(s) - 1];
const nextOf = (s: StepKey) => copy.value.order[copy.value.order.indexOf(s) + 1];
const nextLabel = (s: StepKey) => {
  const next = nextOf(s)!;
  if (next === 'send') return t('mail_campaign_to_send');
  const label = t(`mail_campaign_rail_${next}`);
  return t('mail_campaign_next', { step: label.charAt(0).toLowerCase() + label.slice(1) });
};

// ── Content ─────────────────────────────────────────────────────────────────
// Newsletters start from the marketing starters, except the welcome flow's; visitors get "Wijziging".
const starters = computed(() => STARTER_TEMPLATES.filter(s => (isService.value ? s.key === 'change'
  : isInvitation.value ? s.key === 'invitation' : s.kind === 'marketing' && !['welcome', 'invitation'].includes(s.key))));
const starterName = (s: Starter) => (locale.value === 'nl' ? s.name.nl : s.name.en);
const languageOptions = computed(() => MAIL_LANGUAGES.filter(l => orgLanguages.value.includes(l.code)));
const previewLang = ref(orgLanguages.value[0] ?? 'nl');
const pickOpen = ref(false);
const contentItems = computed(() => [{ label: t('mail_campaign_pick_other'), icon: 'envelope', command: () => { pickOpen.value = true; } }]);
const editorLink = computed(() => ({
  path: `/mail-editor/${templateId.value}`,
  query: { from: `${selfPath.value}?step=content`, label: 'mail_editor_back_campaign' },
}));

// Picking replaces this campaign's own copy; say so when there is one to lose.
const confirmReplace = (run: () => Promise<void>) => {
  if (!selectedTemplate.value?.campaign_id) return run();
  return confirmAction(t('mail_campaign_replace_confirm'), { header: t('mail_campaign_replace_title'), accept: t('mail_campaign_replace') })
    .then((ok) => (ok ? run() : undefined));
};
const busyPick = ref<string | null>(null);
const pickError = ref<string | null>(null);
const pickFailed = (e: unknown) => {
  console.error(e);
  pickError.value = t('mail_campaign_template_failed') + ': ' + friendlyErrorText(e, t);
};
const picked = async (tplId: string) => {
  templateId.value = tplId;
  if (campaign.value) campaign.value.template_id = tplId;
  lastSaved = snapshot();
  await loadTemplate(tplId);
  pickOpen.value = false;
};
const pickStarter = (s: Starter) => confirmReplace(async () => {
  if (!campaign.value) return;
  busyPick.value = s.key;
  pickError.value = null;
  try {
    const tpl = await campaignMailFromStarter({ id: campaign.value.id, name: name.value.trim() || campaign.value.name, kind: campaign.value.kind }, s);
    await picked(tpl.id);
  } catch (e) {
    pickFailed(e);
  } finally {
    busyPick.value = null;
  }
});
const pickDesign = (tpl: MailTemplate) => confirmReplace(async () => {
  if (!campaign.value) return;
  busyPick.value = tpl.id;
  pickError.value = null;
  const { data, error } = await supabase.rpc('mail_campaign_use_template', { p_campaign: campaign.value.id, p_template: tpl.id });
  busyPick.value = null;
  if (error || !data) return pickFailed(error);
  await picked(data);
});

const previewHtml = ref('');
const previewSubject = ref('');
const previewLoading = ref(false);
const previewError = ref(false);
const loadPreview = async () => {
  const tpl = selectedTemplate.value;
  if (!tpl) { previewHtml.value = ''; previewSubject.value = ''; return; }
  previewLoading.value = true;
  previewError.value = false;
  try {
    const result = await preview(tpl.document as unknown as MailDocument, { kind: mailKind.value, lang: previewLang.value, event_id: serviceEventId.value });
    previewHtml.value = result.html;
    previewSubject.value = result.subject;
  } catch {
    previewError.value = true;
  } finally {
    previewLoading.value = false;
  }
};
watch(() => [selectedTemplate.value?.id, selectedTemplate.value?.updated_at, previewLang.value], loadPreview, { immediate: true });

// Languages of the organization where the mail still misses text (they get the first language there)
const missingLanguages = computed(() => {
  const doc = selectedTemplate.value?.document as unknown as MailDocument | undefined;
  const source = orgLanguages.value[0] ?? 'nl';
  if (!doc?.blocks) return [];
  const fields = Object.values(lFields(doc));
  return orgLanguages.value.filter(l => l !== source && fields.some(f => fieldState(f, l, source) === 'missing'))
    .map(l => MAIL_LANGUAGES.find(x => x.code === l)?.label ?? l);
});

// The subject, edited right above the preview: the first language of the mail, the rest stays as it is.
const sourceLang = computed(() => orgLanguages.value[0] ?? 'nl');
const orgName = computed(() => String((userStore.organization as { name?: string } | null)?.name ?? ''));
const subjectDraft = ref('');
const subjectTouched = ref(false);
watch(() => selectedTemplate.value?.id, () => {
  const raw = (selectedTemplate.value?.document as unknown as MailDocument | undefined)?.subject?.[sourceLang.value];
  subjectTouched.value = false;
  // {{organizer.name}} reads as the name in the field; other placeholders stay as they are
  const text = typeof raw === 'string' ? raw : raw?.text ?? '';
  subjectDraft.value = orgName.value ? text.replaceAll('{{organizer.name}}', orgName.value) : text;
}, { immediate: true });
const saveSubject = async () => {
  const tpl = selectedTemplate.value;
  if (!subjectTouched.value || !tpl || !editable.value || !subjectDraft.value.trim()) return;
  const doc = structuredClone(toRaw(tpl.document)) as unknown as MailDocument;
  doc.subject = { ...doc.subject };
  writeText(doc.subject, sourceLang.value, sourceLang.value, subjectDraft.value.trim());
  saveState.value = 'saving';
  const { data, error } = await supabase.from('mail_templates')
    .update({ document: doc as never, updated_by: userStore.user?.id ?? null }).eq('id', tpl.id).select('*').single();
  if (error || !data) {
    saveState.value = 'error';
    console.error(error);
    return false;
  }
  subjectTouched.value = false;
  // a new version: the preview reloads and the test asks to be sent again
  selectedTemplate.value = data;
  saveState.value = 'saved';
  return true;
};
watchDebounced(subjectDraft, saveSubject, { debounce: 800 });
// Leaving within the debounce would lose the edit; the editor would even load the old subject and write it back
onBeforeRouteLeave(async (to) => {
  const subjectSaved = await saveSubject();
  await save();
  if (subjectSaved === false && to.path.startsWith('/mail-editor/')) return false;
});

// ── Test and send ───────────────────────────────────────────────────────────
// A test counts for the exact version of the mail it was sent for (mail_template_tests on the server).
const test = ref<{ sent_to: string; template_updated_at: string | null } | null>(null);
const testDone = computed(() => !!test.value?.template_updated_at && !!selectedTemplate.value
  && new Date(test.value.template_updated_at).getTime() === new Date(selectedTemplate.value.updated_at).getTime());

const postalMissing = computed(() => !!settings.value && !settings.value.postal_address?.trim());
const postal = ref('');
const savingPostal = ref(false);
const postalError = ref<string | null>(null);
const postalOpen = ref(false);
// Saved shows itself: the notice goes and sending opens up.
const savePostal = async () => {
  savingPostal.value = true;
  postalError.value = null;
  try {
    await saveSettings({ postal_address: postal.value.trim() });
    postalOpen.value = false;
  } catch (e) {
    console.error(e);
    postalError.value = t('mail_campaign_save_failed') + ': ' + friendlyErrorText(e, t);
  } finally {
    savingPostal.value = false;
  }
};

const later = ref(false);
const scheduleAt = ref<Date | null>(null);
const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
const timeZoneLabel = computed(() => (timeZone === 'Europe/Amsterdam' ? t('mail_campaign_tz_nl') : timeZone));
const limitReached = computed(() => !!quota.value && quota.value.contacts > quota.value.contact_limit);
const canSchedule = computed(() => canSend.value && !!templateId.value && recipientsReady.value
  && (isService.value || (testDone.value && !postalMissing.value && !limitReached.value))
  && (!later.value || (!!scheduleAt.value && scheduleAt.value.getTime() > Date.now())));

const whenOptions = computed(() => [
  { value: 'now', label: t('mail_campaign_mode_now') },
  { value: 'later', label: t('mail_campaign_mode_later') },
]);
const whenText = computed(() => (later.value && scheduleAt.value
  ? t('mail_campaign_when_at', { when: dateTime(scheduleAt.value.toISOString()) })
  : t('mail_campaign_when_now')));
const quotaExceeded = computed(() => !!quota.value && !!count.value?.reachable
  && quota.value.sent_this_month + count.value.reachable > quota.value.monthly_limit);
// A main button that cannot yet says why, in one line
const sendBlock = computed(() => {
  if (!canSend.value || canSchedule.value) return '';
  if (!templateId.value) return t('mail_campaign_summary_no_mail_step');
  if (!recipientsReady.value) return t('mail_campaign_block_recipients');
  if (!isService.value) {
    if (postalMissing.value) return t('mail_campaign_block_postal');
    if (limitReached.value) return t('mail_campaign_block_limit');
    if (!testDone.value) return t('mail_campaign_send_needs_test');
  }
  return t('mail_campaign_block_when');
});
const recipientsBlock = computed(() => {
  if (recipientsReady.value) return '';
  if (isInvitation.value && recipientMode.value === 'list') {
    if (listUnsaved.value) return t('mail_invitation_list_unsaved');
    return ownList.value ? t('mail_campaign_error_mail_invitation_needs_confirmation') : '';
  }
  return count.value ? t(copy.value.noRecipients) : '';
});

const ERROR_CODES = ['not_allowed', 'mail_campaign_not_draft', 'mail_not_enabled', 'mail_campaign_without_template',
  'mail_postal_address_missing', 'mail_contact_limit', 'mail_campaign_no_recipients', 'mail_monthly_limit',
  'mail_service_needs_event', 'mail_template_locked', 'mail_invitation_needs_discount', 'mail_invitation_needs_confirmation',
  'mail_campaign_not_found', 'mail_campaign_not_invitation', 'mail_list_invalid', 'mail_campaign_not_sent'];
const errorText = (message: string) => {
  const code = ERROR_CODES.find(c => message.includes(c));
  return code ? t(`mail_campaign_error_${code}`) : t('mail_campaign_error_unknown');
};
const scheduleError = ref<string | null>(null);
const scheduleErrorText = computed(() => (scheduleError.value === 'save_failed' ? t('mail_campaign_save_failed')
  : scheduleError.value && ERROR_CODES.includes(scheduleError.value)
  ? t(`mail_campaign_error_${scheduleError.value}`)
  : t('mail_campaign_error_unknown')));

const scheduling = ref(false);
const confirmSchedule = () => {
  const n = count.value?.reachable ?? 0;
  const params = { n: num(n), when: `${dateTime(scheduleAt.value?.toISOString() ?? null)} (${timeZoneLabel.value})` };
  const message = later.value ? t(copy.value.confirmLater, params, n) : t(copy.value.confirmNow, params, n);
  confirmAction(message, {
    header: later.value ? t(copy.value.confirmLaterTitle, params, n) : t(copy.value.confirmNowTitle, params, n),
    accept: later.value ? t('mail_campaign_schedule') : t('mail_campaign_send_now'),
    // sending now cannot be called back; a scheduled one can until it starts
    danger: !later.value,
  }).then((ok) => { if (ok) schedule(); });
};
const schedule = async () => {
  if (!campaign.value) return;
  scheduling.value = true;
  scheduleError.value = null;
  // Never schedule what was not saved: the old recipients or mail would go out
  if (await saveSubject() === false || await save() === false) {
    scheduling.value = false;
    scheduleError.value = 'save_failed';
    return;
  }
  const { error } = await supabase.rpc('mail_campaign_schedule', {
    p_campaign_id: campaign.value.id,
    ...(later.value && scheduleAt.value ? { p_at: scheduleAt.value.toISOString() } : {}),
  });
  scheduling.value = false;
  if (error) {
    scheduleError.value = ERROR_CODES.find(code => error.message.includes(code)) ?? error.message;
    return;
  }
  // The page turns into the scheduled/sending view with the recipients, so no message
  await Promise.all([loadCampaign(), isService.value ? null : loadQuota()]);
};

// ── How it went (after scheduling): mail_send_counts, grouped ──────────────
const counts = ref<{ status: string; skip_reason: string | null; n: number }[] | null>(null);
const loadCounts = async () => {
  if (!campaign.value || campaign.value.status === 'draft') return;
  if (isInvitation.value) loadReport();
  const { data } = await supabase.rpc('mail_send_counts', { p_campaign: campaign.value.id });
  counts.value = data ?? [];
};
const sum = (...statuses: string[]) => (counts.value ?? []).filter(c => statuses.includes(c.status)).reduce((a, c) => a + c.n, 0);
const totalCount = computed(() => (counts.value ?? []).reduce((a, c) => a + c.n, 0));
const pendingCount = computed(() => sum('queued', 'sending'));
const doneCount = computed(() => totalCount.value - pendingCount.value);
const progressPercent = computed(() => (totalCount.value ? Math.round((doneCount.value / totalCount.value) * 100) : 0));
const finished = computed(() => !!counts.value && totalCount.value > 0 && pendingCount.value === 0);
const arrived = computed(() => sum('sent', 'delivered', 'complained'));
const notWorking = computed(() => sum('bounced', 'failed'));
const skipped = computed(() => {
  const byReason: Record<string, number> = {};
  for (const c of counts.value ?? []) if (c.status === 'skipped') byReason[c.skip_reason ?? 'unknown'] = (byReason[c.skip_reason ?? 'unknown'] ?? 0) + c.n;
  return Object.entries(byReason).map(([reason, n]) => ({ reason, n })).sort((a, b) => b.n - a.n);
});

const active = computed(() => ['scheduled', 'sending'].includes(campaign.value?.status ?? ''));
watch(() => campaign.value?.status, (status) => {
  counts.value = null;
  if (status && status !== 'draft') loadCounts();
}, { immediate: true });

let timer: ReturnType<typeof setInterval> | undefined;
watch(active, (on) => {
  clearInterval(timer);
  if (on) timer = setInterval(async () => { await loadCampaign(); await loadCounts(); }, 5000);
}, { immediate: true });
onBeforeUnmount(() => clearInterval(timer));

const canCancel = computed(() => canSend.value && campaign.value?.status === 'scheduled' && !!counts.value && doneCount.value === 0);
const cancelling = ref(false);
// Stopping a planned one can be undone, so it happens at once: back to draft, "Ongedaan maken" plans it again
const unschedule = async () => {
  const c = campaign.value;
  if (!c) return;
  const at = c.scheduled_at;
  cancelling.value = true;
  try {
    await undo.run({
      do: async () => {
        const { error } = await supabase.rpc('mail_campaign_cancel', { p_campaign_id: c.id });
        if (error) throw error;
        await Promise.all([loadCampaign(), isService.value ? null : loadQuota()]);
        step.value = 'send';
      },
      undo: async () => {
        const future = at && new Date(at).getTime() > Date.now() + 60_000;
        const { error } = await supabase.rpc('mail_campaign_schedule', { p_campaign_id: c.id, ...(future ? { p_at: at } : {}) });
        if (error) throw error;
        await loadCampaign();
      },
      message: t('mail_campaign_unscheduled'),
    });
  } catch (e) {
    const error = e as { message?: string };
    showSupabaseError(toast, error, {
      summary: t('mail_campaign_cancel_failed'),
      detail: error.message?.includes('mail_campaign_already_sending') ? t('mail_campaign_error_already_sending') : undefined,
      life: 6000,
    });
    await loadCounts();
  } finally {
    cancelling.value = false;
  }
};

// ── Invitation: who gets it ─────────────────────────────────────────────────
// The own list lives in segment.contact_ids (mail_campaign_set_list); a reminder of a consent
// invitation carries contact_ids too, with consent_only.
type RecipientMode = 'consent' | 'group' | 'list';
const isOwnList = (s: Segment) => {
  const raw = s as { contact_ids?: unknown; consent_only?: boolean };
  return Array.isArray(raw.contact_ids) && !raw.consent_only;
};
const modeOf = (s: Segment): RecipientMode => (isOwnList(s) ? 'list' : Object.keys(s).length ? 'group' : 'consent');
const recipientMode = ref<RecipientMode>('consent');
const recipientModes = computed(() => [
  { value: 'consent', label: t('mail_invitation_mode_consent') },
  { value: 'group', label: t('mail_invitation_mode_group') },
  { value: 'list', label: t('mail_invitation_mode_list') },
]);
const ownList = computed(() => isOwnList(segment.value));
// Leaving the own list starts from everyone with consent again; the list itself waits for "Opslaan"
const setRecipientMode = (m: RecipientMode) => {
  recipientMode.value = m;
  if (m === 'consent' || (m === 'group' && 'contact_ids' in segment.value)) segment.value = {};
};
// An own list goes out only once you confirmed these people expect it
// and only the list in the box: text that differs from the saved list would not be who gets it
const recipientsReady = computed(() => !!count.value?.reachable
  && (!isInvitation.value || recipientMode.value !== 'list' || (ownList.value && !!campaign.value?.list_confirmed_at && !listUnsaved.value)));

// Counted as an invitation: an own list needs no consent, unsubscribed and bounced never
const countInvitation = async () => {
  if (!isInvitation.value || !organizationId.value) return;
  const { data, error } = await supabase.rpc('mail_segment_count', { p_org: organizationId.value, p_segment: segment.value as never, p_kind: 'invitation' });
  count.value = error ? null : ((data as SegmentCount[] | null)?.[0] ?? { total: 0, reachable: 0 });
};
watchDebounced([isInvitation, segment], countInvitation, { debounce: 400, deep: true, immediate: true });

const listText = ref('');
const savedListText = ref('');
const listUnsaved = computed(() => listText.value.trim() !== savedListText.value.trim());
const listFile = ref<HTMLInputElement | null>(null);
const savingList = ref(false);
const listError = ref<string | null>(null);
const listResult = ref<{ saved: number; new_contacts: number; unreachable: number; invalid: string[] } | null>(null);
// One person per line: "Naam <adres>", an address, or CSV cells (the cell with an @ is the address,
// the first other cell the name). A line without @ goes as it is, so the server names it as wrong.
// shortcut: no quoted commas inside a CSV cell; papaparse when lists come from real spreadsheets.
const parseList = (text: string) => text.split(/\r?\n/).flatMap((line) => {
  const trimmed = line.trim();
  if (!trimmed) return [];
  if (trimmed.includes('<')) return [trimmed];
  const cells = trimmed.split(/[,;\t]/).map(c => c.trim().replace(/^"|"$/g, '')).filter(Boolean);
  const emails = cells.filter(c => c.includes('@'));
  if (!emails.length) return [trimmed];
  if (emails.length > 1) return emails;
  const name = cells.find(c => !c.includes('@'));
  return [name ? { email: emails[0]!, name } : emails[0]!];
});
// A CSV lands in the field (header row left out), so you see what will be saved
const onListFile = async (e: Event) => {
  const input = e.target as HTMLInputElement;
  const file = input.files?.[0];
  input.value = '';
  if (!file) return;
  const lines = (await file.text()).split(/\r?\n/).filter(l => l.trim());
  if (lines[0] && !lines[0].includes('@')) lines.shift();
  listText.value = lines.map(l => parseList(l).map(r => (typeof r === 'string' ? r : `${r.name} <${r.email}>`)).join('\n')).join('\n');
};
const saveList = async () => {
  if (!campaign.value) return;
  savingList.value = true;
  listError.value = null;
  // the name and the rest first: reloading below takes what is stored
  if (await save() === false) {
    listError.value = t('mail_campaign_save_failed');
    savingList.value = false;
    return;
  }
  const text = listText.value;
  const { data, error } = await supabase.rpc('mail_campaign_set_list', { p_campaign_id: campaign.value.id, p_recipients: parseList(text) as never });
  if (error) listError.value = errorText(error.message);
  else {
    savedListText.value = text;
    listResult.value = data as typeof listResult.value;
    await loadCampaign();
  }
  savingList.value = false;
};
const confirmingList = ref(false);
const confirmList = async (on: boolean) => {
  if (!campaign.value) return;
  confirmingList.value = true;
  const at = on ? new Date().toISOString() : null;
  const { error } = await supabase.from('mail_campaigns').update({ list_confirmed_at: at }).eq('id', campaign.value.id);
  confirmingList.value = false;
  if (error) return showSupabaseError(toast, error, { summary: t('mail_campaign_save_failed'), life: 6000 });
  campaign.value.list_confirmed_at = at;
};

// ── Invitation: who bought, a reminder, the codes left ──────────────────────
type ReportRow = Database['public']['Functions']['mail_invitation_report']['Returns'][number];
const ARRIVED = ['sent', 'delivered', 'complained'];
const report = ref<ReportRow[] | null>(null);
const loadReport = async () => {
  if (!campaign.value) return;
  const { data } = await supabase.rpc('mail_invitation_report', { p_campaign_id: campaign.value.id });
  report.value = data ?? [];
};
const boughtCount = computed(() => (report.value ?? []).filter(r => r.order_id).length);
const mailedCount = computed(() => (report.value ?? []).filter(r => r.order_id || ARRIVED.includes(r.status)).length);
const notBoughtCount = computed(() => (report.value ?? []).filter(r => !r.order_id && ['sent', 'delivered'].includes(r.status)).length);
const unusedCodes = computed(() => (report.value ?? []).filter(r => r.code && !r.order_id));

const exportUnused = () => {
  const rows = [[t('email'), t('name'), t('code')], ...unusedCodes.value.map(r => [r.email, r.name ?? '', r.code])];
  // A name from a pasted list could start with = + - @: prefix a quote so a spreadsheet never runs it
  const cell = (v: unknown) => { const s = String(v); return /^[=+\-@\t\r]/.test(s) ? `'${s}` : s; };
  const csv = rows.map(r => r.map(v => `"${cell(v).replace(/"/g, '""')}"`).join(';')).join('\n');
  const a = document.createElement('a');
  a.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
  a.download = `codes-${campaign.value?.name ?? 'uitnodiging'}.csv`;
  a.click();
  URL.revokeObjectURL(a.href);
};

const reminding = ref(false);
const remind = async () => {
  if (!campaign.value) return;
  reminding.value = true;
  // A reminder not sent yet opens again: a second one would mail the same people twice
  const root = campaign.value.parent_campaign_id ?? campaign.value.id;
  const { data: open } = await supabase.from('mail_campaigns').select('id').eq('parent_campaign_id', root)
    .in('status', ['draft', 'scheduled']).order('created_at', { ascending: false }).limit(1).maybeSingle();
  if (open) {
    reminding.value = false;
    return navigateTo(`/mail/campaigns/${open.id}`);
  }
  const { data, error } = await supabase.rpc('mail_campaign_remind', { p_campaign_id: campaign.value.id });
  reminding.value = false;
  if (error || !data) return showSupabaseError(toast, error, { summary: t('mail_invitation_remind_failed'), detail: error ? errorText(error.message) : undefined, life: 6000 });
  navigateTo(`/mail/campaigns/${data}`);
};

watch([organizationId, id], load, { immediate: true });
</script>
